import type { FieldExplainerContent } from './types';

type Debt = { name: string; balance: number; rate: number; minPayment: number };

export type DebtPayoffExplainerContext = {
  input: {
    debts: Debt[];
    extraPayment: number;
  };
  result: {
    selectedStrategy: 'minimum' | 'avalanche' | 'snowball';
    selectedPaidOff: boolean;
    cappedButShrinking: boolean;
    debtGrowing: boolean;
    canCompareVsMinimum: boolean;
    monthsToFreedomLabel: string;
    monthsSavedVsMinimum: number;
    totalInterestLabel: string;
    interestSavedVsMinimum: number;
    totalStartingDebt: number;
    minimumMonths: number;
    minimumInterest: number;
  };
};

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

// A zero-balance row never receives targeted payments, so it has no place in
// the displayed priority order.
function activeDebts(debts: Debt[]): Debt[] {
  return debts.filter((debt) => debt.balance > 0);
}

function avalancheOrder(debts: Debt[]): Debt[] {
  return activeDebts(debts).sort((a, b) => b.rate - a.rate);
}

function snowballOrder(debts: Debt[]): Debt[] {
  return activeDebts(debts).sort((a, b) => a.balance - b.balance);
}

export function getDebtPayoffFieldExplainers(ctx: DebtPayoffExplainerContext): FieldExplainerContent[] {
  const { debts, extraPayment } = ctx.input;
  const {
    selectedStrategy,
    selectedPaidOff,
    cappedButShrinking,
    debtGrowing,
    canCompareVsMinimum,
    monthsToFreedomLabel,
    monthsSavedVsMinimum,
    totalInterestLabel,
    interestSavedVsMinimum,
    totalStartingDebt,
    minimumMonths,
    minimumInterest
  } = ctx.result;

  const totalMinPayments = debts.reduce((sum, debt) => sum + debt.minPayment, 0);
  const monthlyBudget = totalMinPayments + extraPayment;

  const strategyFormula = (() => {
    if (selectedStrategy === 'minimum') {
      return `${debts.length} debt${debts.length === 1 ? '' : 's'}, each paying min(minimum, balance) every month\nno freed-up minimum ever rolls to another debt`;
    }
    if (selectedStrategy === 'avalanche') {
      const order = avalancheOrder(debts);
      return `priority by APR: ${order.map((d) => `${d.name} (${d.rate}%)`).join(' → ')}\nfirst target: ${order[0]?.name ?? '—'}\n(the schedule re-checks this order every month\nas minimums and payoffs move the balances)`;
    }
    const order = snowballOrder(debts);
    return `priority by balance: ${order.map((d) => `${d.name} (${usd.format(d.balance)})`).join(' → ')}\nfirst target: ${order[0]?.name ?? '—'}\n(the schedule re-checks this order every month\nas minimums and payoffs move the balances)`;
  })();

  const strategyPlain = (() => {
    if (selectedStrategy === 'minimum') {
      return 'The do-nothing-extra baseline: every debt pays only its own minimum, forever, with no rollover. Avalanche and snowball are both measured against this.';
    }
    if (selectedStrategy === 'avalanche') {
      return 'The fixed monthly budget — every minimum plus your extra payment — is aimed at the highest-APR debt first. Once that debt clears, its whole share cascades to the next-highest rate the same month.';
    }
    return 'The fixed monthly budget is aimed at the smallest-balance debt first. Once it clears, its whole share cascades to the next-smallest balance the same month.';
  })();

  const timeToDebtFreePlain = (() => {
    if (selectedPaidOff) {
      return 'The month the schedule shows every debt at zero, stepping forward one month at a time with interest compounded monthly on whatever balance is left.';
    }
    if (cappedButShrinking) {
      return 'The schedule stops projecting at 600 months. At these payments the total balance is still going down each month, just too slowly to hit zero inside that window — so this is a floor, not an exact payoff date.';
    }
    return "By the end of the 600-month projection the total balance has stopped falling — the interest being added is outpacing what these payments clear — so there is no payoff month to report. The balance may still dip in earlier years while healthier debts clear before the underpaid one takes over.";
  })();

  const timeToDebtFreeFormula = (() => {
    if (selectedPaidOff) {
      return `${monthsToFreedomLabel} to clear ${debts.length} debt${debts.length === 1 ? '' : 's'}${
        canCompareVsMinimum ? `\n${monthsSavedVsMinimum} months faster than minimum-only (${minimumMonths} months)` : ''
      }`;
    }
    if (cappedButShrinking) {
      return 'balance is lower at month 600 than 12 months earlier,\nbut has not reached $0 — payoff falls outside this tool\'s 600-month window';
    }
    return 'balance at month 600 is no lower than at month 588,\nso the schedule is not converging on zero\ninside the 600-month window';
  })();

  const totalInterestPlain = (() => {
    if (selectedPaidOff) {
      return 'Every dollar of interest charged across the whole payoff schedule, added up one month at a time until the last debt hits zero under this strategy.';
    }
    if (cappedButShrinking) {
      return "The interest actually accrued in the 600 months this calculator modeled. Since the balance has not reached zero yet, this is a running total so far, not the true lifetime interest cost.";
    }
    return 'With payments not covering the interest that accrues, this figure would only climb without bound, so no dollar total is shown.';
  })();

  const totalInterestFormula = (() => {
    if (selectedPaidOff) {
      return `sum of interest charged each month over ${monthsToFreedomLabel}\n= ${totalInterestLabel}${
        canCompareVsMinimum ? `\n${usd.format(interestSavedVsMinimum)} less than minimum-only (${usd.format(minimumInterest)})` : ''
      }`;
    }
    if (cappedButShrinking) {
      return `sum of interest charged in the 600 months modeled\n= ${totalInterestLabel} (still counting)`;
    }
    return 'interest added each month keeps compounding on a balance\nthat is not shrinking — shown as "Rises indefinitely" instead of a number';
  })();

  return [
    {
      id: 'extraPayment',
      kind: 'input',
      label: 'Extra monthly payment',
      plain: "Cash added on top of every debt's minimum. Under avalanche and snowball it joins a shared monthly budget that pays each debt's minimum first, then waterfalls whatever is left toward the strategy's current target — cascading to the next target the moment one clears.",
      formula: `budget = ${usd.format(totalMinPayments)} minimums + ${usd.format(extraPayment)} extra\n       = ${usd.format(monthlyBudget)} per month`,
      feeds: 'How fast avalanche and snowball clear any debt beyond whichever minimums are already scheduled to be paid.',
      assumption: 'The minimum-only baseline never receives this money — it exists only to show what standing still would cost, so every "vs minimum" comparison on this page is against a plan that ignores this field entirely.'
    },
    {
      id: 'selectedStrategy',
      kind: 'result',
      label: 'Selected strategy',
      plain: strategyPlain,
      formula: strategyFormula,
      feeds: 'The chart, the payoff-milestone table, and the monthly timeline below — all three redraw from whichever strategy is selected here.'
    },
    {
      id: 'timeToDebtFree',
      kind: 'result',
      label: 'Time to debt-free',
      plain: timeToDebtFreePlain,
      formula: timeToDebtFreeFormula,
      feeds: 'The payoff-milestone table (each debt\'s own clear month) and the x-axis of the balance chart.',
      assumption: !selectedPaidOff
        ? 'Every debt keeps its entered APR and minimum unchanged for the full 600-month projection — no rate changes, no missed payments.'
        : undefined
    },
    {
      id: 'totalInterest',
      kind: 'result',
      label: 'Total interest',
      plain: totalInterestPlain,
      formula: totalInterestFormula,
      feeds: 'The "saved vs minimum" note beneath this card, when the minimum-only baseline also pays off.'
    },
    {
      id: 'totalStartingDebt',
      kind: 'result',
      label: 'Total starting debt',
      plain: "The sum of every balance you've entered, before any interest or payment is applied. It doesn't feed the payoff math itself — the strategies work debt by debt — but it's the starting line every payoff timeline on this page is measured against.",
      formula: `${debts.map((debt) => usd.format(debt.balance)).join(' + ')}\n= ${usd.format(totalStartingDebt)}`,
      feeds: 'Nothing else on the page directly; it is a reference figure alongside the strategy results.'
    }
  ];
}
